/* eslint-disable react-hooks/exhaustive-deps */
import { captureException } from "extensions/sentry";
import { toast } from "extensions/toast";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import { CancelButton, PrimaryButton } from "common/components/buttons";
import { OptionsButton } from "common/components/buttons/optionsButton";
import { CheckBox } from "common/components/checkbox";
import { ErrorView } from "common/components/error";
import { request } from "common/components/request";
import { emitter } from "common/emitter";
import { SCHEDULE_AUTOMATIC_DOWNLOAD } from "common/reducers/downloads";
import { store } from "common/store";

import { faBell } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const submitExportRequest = function(payload, shouldStartDownloadAutomatically) {
    const _payload = {
        filters: { ...payload?.filters },
        ...payload
    };

    // At this point we can convert qualifiers to encoded string
    if (Object.prototype.hasOwnProperty.call(_payload.filters, "q")) {
        _payload.filters.q = _payload.filters.q.toString(true);
    }

    request("/merchant/:merchant_ref/user/export", {
        method: "POST",
        data: _payload
    }).then((data) => {
        if (shouldStartDownloadAutomatically) {
            store.dispatch(SCHEDULE_AUTOMATIC_DOWNLOAD(data.job_ref));
        }

        const showNotifications = (e) => {
            e.preventDefault();
            emitter.emit("open.downloadsPanel");
        };
        toast.success((
            <div>Your file will appear in the <strong><FontAwesomeIcon icon={faBell}/> <a className="alert-link" onClick={showNotifications} href="#">notifications panel</a></strong> when ready for download</div>
        ));
    }).catch((error) => {
        toast.error("Unable to make an export");
        captureException(error);
    });
};

export function ExportDialog(props) {
    const exportOptions = props.getExportOptions();
    const [state, setState] = useState({
        fileName: exportOptions.suggested_name,
        content_type: exportOptions.formats[0].value,
        resource: exportOptions.content[0].resource,
        automaticDownload: props.haveAutomaticDownload,
        selected: [props.section],
        selectAllSections: false,
        status: "loading"
    });

    useEffect(() => {
        setState({
            ...state,
            status: "idle"
        });
    }, []);

    const handleExport = () => {
        try {
            const payload = prepareExport();
            if (payload.content.length) {
                submitExportRequest(payload, state.automaticDownload);
                props.close();
            } else {
                toast.warning("It looks like you did not choose content to export.");
            }
        } catch (error) {
            setState({ ...state, status: "error", error });
            captureException(error);
        }
    };

    const handleChange = (e) => {
        setState({
            ...state,
            [e.target.name]: e.target.value
        });
    };

    const handleSectionSelect = (e) => {
        const { name } = e.target;
        if (!state.selected.includes(name)) {
            addToSelected(name);
        } else {
            removeFromSelected(name);
        }
    };

    const handleFormatChange = (content_type) => setState({ ...state, content_type });

    const handleResourceChange = (resource) => setState({ ...state, resource, selected: [] });

    const addToSelected = (name) => setState({ ...state, selected: [...state.selected, name] });

    const removeFromSelected = (name) => setState({ ...state, selected: state.selected.filter((item) => item !== name) });

    const prepareExport = () => {
        let content = exportOptions.content.find(({ resource }) => resource === state.resource);
        if (!state.selectAllSections && content.resource !== "html") {
            content = content.structure.filter(({ value }) => state.selected.includes(value));
        } else {
            return {
                name: state.fileName,
                content_type: state.content_type,
                content: [...content.structure],
                filters: { ...exportOptions.filters },
                resource: state.resource
            };
        }

        // Merge filterbar qualifiers with selected rows for export
        const summary_ids = props.getSelected();

        return {
            name: state.fileName,
            content_type: state.content_type,
            content,
            filters: { ...exportOptions.filters, summary_ids },
            resource: state.resource
        };
    };

    const renderActions = () => (
        <div className="actions">
            <div className="group" />
            <div className="group">
                <CancelButton onClick={props.close}>Cancel</CancelButton>
                <PrimaryButton onClick={handleExport}>Export</PrimaryButton>
            </div>
        </div>
    );

    if (state.status === "error") {
        return (
            <div className="configurable-table-export table-export">
                <ErrorView
                    error={state.error}
                    header="Export failed"
                    message="Something went wrong during the export. Try to refresh page."
                    hideDetails
                    captureInSentry
                    custom
                    actions={false}
                />
            </div>
        );
    }
    if (state.status === "loading") {
        return <p>loading</p>;
    }

    const formats = exportOptions.formats.map((format) => ({ ...format, onClick: handleFormatChange })) || [];
    const resources = exportOptions.content.map(({ name, resource }) => ({
        active: true,
        label: name,
        value: resource,
        onClick: handleResourceChange
    }));

    return (
        <div className="configurable-table-export table-export">
            <div className="file-name">
                <div className="field-name">File name</div>
                <input type="text" className="form-control fileName" name="fileName" value={state.fileName} onChange={handleChange} />
            </div>
            {resources.length > 1 && (
                <div key="content" className="format-options">
                    <div className="field-name">Content</div>
                    <OptionsButton structure={resources} activeValue={state.resource} />
                </div>
            )}
            <div key="file_format" className="format-options">
                <div className="field-name">File format</div>
                <OptionsButton structure={formats} activeValue={state.content_type} />
            </div>
            {state.resource !== "html" && (
                <div className="content-options">
                    <div className="bottom-bordered">
                        <CheckBox label="Select all" value={state.selectAllSections} onChange={handleChange} name="selectAllSections" />
                    </div>
                    <div className="options bottom-bordered">
                        {exportOptions.content.find(({ resource }) => resource === state.resource).structure.map((section) => (
                            <CheckBox
                                key={`${state.resource}-${section.value}`}
                                label={section.name}
                                onChange={handleSectionSelect}
                                name={section.value}
                                value={state.selected.includes(section.value) || state.selectAllSections}
                            />
                        ))}
                    </div>
                </div>
            )}
            {props.haveAutomaticDownload && (
                <CheckBox label="Automatically download when ready" value={state.automaticDownload} onChange={handleChange} name="automaticDownload"/>
            )}
            {renderActions()}
        </div>
    );
}

ExportDialog.propTypes = {
    close: PropTypes.func.isRequired,
    getExportOptions: PropTypes.func.isRequired,
    getSelected: PropTypes.func,
    haveAutomaticDownload: PropTypes.bool,
    section: PropTypes.string.isRequired
};

ExportDialog.defaultProps = {
    getSelected: () => [],
    haveAutomaticDownload: false
};
